import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MapWallDisplayFacade } from '@app/features/operating-picture/map-wall-display/store';
import { DatasetSubtype, DatasetType } from '@app/modules/core/store/models/dataset.model';
import { TranslatePipe } from '@ngx-translate/core';
import { AlertCardComponent, AlertCardConfig } from './alert-card/alert-card.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, skip, take } from 'rxjs/operators';
import { AlertsFeedSettings } from '../../../models/alerts-feed-settings.model';

@Component({
  selector: 'topo-alert-feed-widget',
  imports: [TranslatePipe, AlertCardComponent],
  templateUrl: './alert-feed-widget.component.html',
  styleUrl: './alert-feed-widget.component.scss'
})
export class AlertFeedWidgetComponent implements OnInit, OnDestroy {
  protected state = inject(MapWallDisplayFacade);

  protected totalCounts = computed(() => {
    const summaries = this.state.workflowStatusSummaries() ?? [];

    return summaries.reduce(
      (acc, { statusCounts = { inProgress: 0, pending: 0 } }) => {
        acc.pending += statusCounts.pending;
        acc.inProgress += statusCounts.inProgress;
        return acc;
      },
      {
        pending: 0,
        inProgress: 0
      }
    );
  });

  private prohibitedSubtypes = new Set([DatasetSubtype.HorizonGeospatialThreatZone, DatasetSubtype.HorizonLocationIntelRatings]);

  alerts = computed(() => {
    const { alertsFeedSettings = {} } = this.state.configuration();

    const { respectLayerVisibility = false, includedLayers = {} } = alertsFeedSettings as AlertsFeedSettings;

    const { layers = [], datasetsSet = {}, layerStates = {} } = this.state.mapStateWithDebounce() ?? {};

    const wfSummaries = this.state.workflowStatusSummaries();

    const alertDocs = layers.reduce((acc, layer) => {
      const shouldExclude = typeof includedLayers[layer.uId] === 'boolean' ? !includedLayers[layer.uId] : false;

      if (shouldExclude) {
        return acc;
      }

      const layerState = layerStates[layer.uId];

      if (respectLayerVisibility && !layerState.isVisible) {
        return acc;
      }

      const dataset = datasetsSet[layer.datasetUId];

      if (Boolean(dataset) && dataset.type === DatasetType.Alerts && !this.prohibitedSubtypes.has(dataset.subtype)) {
        if (Boolean(layerState)) {
          const documents = Object.values(layerState?.documents ?? {});

          documents.forEach(document => {
            if (respectLayerVisibility) {
              const wfSummary = wfSummaries.find(({ sectionUId }) => sectionUId === layer.folderUId);

              if (!!wfSummary) {
                const statusSummary = wfSummary.summaryByStatus.find(
                  summary => summary.workflowStepUIds[layer.uId] === document.workflow_step_uid
                );

                if (!!statusSummary) {
                  const statusLayerDoc = statusSummary.layerDocs.find(l => l.layerUId === layer.uId);

                  if (!!statusLayerDoc) {
                    const { isVisible, isWorkflowStepHidden } = statusLayerDoc;

                    if (!isVisible || isWorkflowStepHidden) {
                      return;
                    }
                  }
                }
              }
            }

            acc.push({
              uId: document.uid,
              documentPartial: document,
              layer,
              dataset
            });
          });
        }
      }

      return acc;
    }, [] as AlertCardConfig[]);

    const newestAlertDocs = alertDocs
      .sort((a, b) => new Date(b.documentPartial.date_updated).getTime() - new Date(a.documentPartial.date_updated).getTime())
      .slice(0, 5);
    return newestAlertDocs;
  });

  protected isReady = signal(false);

  private resizeObserver: ResizeObserver;

  constructor() {
    if (this.state.isMapInWorkbenchMode()) {
      this.isReady.set(true);
    } else {
      const mapFirstLoad$ = toObservable(this.state.isMapLayersLoading).pipe(
        filter(isMapLayersLoading => !isMapLayersLoading),
        skip(1),
        take(1)
      );

      mapFirstLoad$.subscribe(() => {
        this.isReady.set(true);
      });
    }
  }

  ngOnInit(): void {
    const wrapper = document.getElementById('alert-feed-widget');
    const content = document.getElementById('alert-feed-scaled-container');

    this.resizeObserver = new ResizeObserver(() => {
      content.style.transform = 'scale(1, 1)';

      const { width: wrapperWidth, height: wrapperHeight } = wrapper.getBoundingClientRect();
      const { width: contentWidth, height: contentHeight } = content.getBoundingClientRect();

      const scaleSize = Math.min(wrapperWidth / contentWidth, wrapperHeight / contentHeight);

      content.style.transform = `scale(${scaleSize}, ${scaleSize})`;
    });

    this.resizeObserver.observe(wrapper);
    this.resizeObserver.observe(content);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
